import React from "react";
import { Box, Tabs, Tab, MenuItem, ListSubheader, Divider } from "@mui/material";
import {
  LocationOn as LocationIcon,
  VisibilityOff as VisibilityOffIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { splitLocations } from "../../utils/locationUtils";

const ALL = "all";
const SEPARATOR = "separator";

const tabsSx = {
  borderBottom: 1,
  borderColor: "divider",
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 500,
    minHeight: 42,
  },
};

const LocationTabLabel = ({ location }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {location.is_main_location && (
      <StarIcon fontSize="small" sx={{ color: "warning.main" }} titleAccess="Główna lokalizacja" />
    )}
    <Box component="span" sx={{ fontWeight: location.is_main_location ? 700 : 500 }}>
      {location.name}
    </Box>
    {!location.show_in_timetable && (
      <VisibilityOffIcon fontSize="small" sx={{ color: "text.disabled", ml: 0.5 }} titleAccess="Ukryta w grafiku publicznym" />
    )}
  </Box>
);

/**
 * Location filter tabs for admin screens: "Wszystkie lokalizacje", then the
 * main locations on the left, a separator, then the other locations.
 * `value` is the selected location id, or null for all locations.
 */
export function LocationTabs({ locations, value, onChange }) {
  const { main, other } = splitLocations(locations);
  const known = locations.some((location) => location.id === value);

  return (
    <Tabs
      value={value != null && known ? value : ALL}
      onChange={(_, newValue) => onChange(newValue === ALL ? null : newValue)}
      variant="scrollable"
      scrollButtons="auto"
      sx={tabsSx}
    >
      <Tab value={ALL} label="Wszystkie lokalizacje" icon={<LocationIcon fontSize="small" />} iconPosition="start" />
      {main.map((location) => (
        <Tab key={location.id} value={location.id} label={<LocationTabLabel location={location} />} />
      ))}
      {main.length > 0 && other.length > 0 && (
        <Tab
          value={SEPARATOR}
          disabled
          sx={{ minWidth: 0, px: 1.5, opacity: "1 !important" }}
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", fontSize: "0.75rem" }}>
              <Divider orientation="vertical" flexItem sx={{ height: 24 }} />
              Pozostałe
            </Box>
          }
        />
      )}
      {other.map((location) => (
        <Tab key={location.id} value={location.id} label={<LocationTabLabel location={location} />} />
      ))}
    </Tabs>
  );
}

/**
 * Options for a location <Select>: main locations first under their own
 * header, then the rest. Returns an array so it can follow fixed options
 * like "Wszystkie lokalizacje" inside the same Select.
 */
export function renderLocationMenuItems(locations, getLabel = (location) => location.name) {
  const { main, other } = splitLocations(locations);
  const item = (location) => (
    <MenuItem key={location.id} value={location.id}>
      {location.is_main_location && (
        <StarIcon fontSize="small" sx={{ color: "warning.main", mr: 1 }} />
      )}
      {getLabel(location)}
    </MenuItem>
  );

  if (main.length === 0) return other.map(item);
  return [
    <ListSubheader key="main-header">Główne lokalizacje</ListSubheader>,
    ...main.map(item),
    ...(other.length > 0
      ? [<ListSubheader key="other-header">Pozostałe lokalizacje</ListSubheader>, ...other.map(item)]
      : []),
  ];
}
